import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {environment} from '../../env';
import {AppLogger, AppLoggerService} from '../../core/logging';
import {parseDecimal} from '../../utils/number.util';
import {
  CheckResponse,
  DEFAULT_REMINDER_SETTINGS,
  QuickCheck,
  QuickCheckStock,
  ReminderSettings,
  RespondResult,
} from '../../models/consumption-check';

/**
 * Transport for consumption reminders, written against the documented phase-1
 * REST contract. This is the ONLY place snake_case ↔ camelCase mapping happens;
 * everything above speaks the view model.
 *
 * Note the endpoint split, matching shopping lists and meal-plan entries: reads
 * are household-scoped, answering is check-addressed
 * (`/consumption-checks/{id}/respond`).
 */

// --- API wire shapes (snake_case, as the backend returns them) ---

interface ApiStock {
  id: number;
  product_id: number;
  product_name: string;
  thumb_image_url?: string;
  quantity_left?: string | number;
  quantity_full?: string | number;
  unit?: string;
  store_name?: string;
  added_at?: string;
  is_open?: boolean;
  product_total?: string | number;
  product_stock_count?: number;
}

interface ApiCheck {
  id: number;
  state: QuickCheck['state'];
  asked_at: string;
  expires_at: string;
  question?: string;
  question_key?: string;
  params?: Record<string, string>;
  stock: ApiStock | null;
  responses?: CheckResponse[];
}

interface ApiCheckListResponse {
  checks?: ApiCheck[];
}

interface ApiRespondResponse {
  id: number;
  state: QuickCheck['state'];
  response: CheckResponse;
  answered_at?: string;
  stock: ApiStock | null;
}

interface ApiSettings {
  timezone?: string;
  consumption_reminders_enabled?: boolean;
  consumption_cadence_days?: number;
  consumption_items_per_round?: number;
  quiet_hours?: {start?: number; end?: number};
}

function settingsFromApi(s: ApiSettings | null | undefined): ReminderSettings {
  return {
    timezone: s?.timezone || DEFAULT_REMINDER_SETTINGS.timezone,
    enabled: s?.consumption_reminders_enabled ?? DEFAULT_REMINDER_SETTINGS.enabled,
    cadenceDays: s?.consumption_cadence_days ?? DEFAULT_REMINDER_SETTINGS.cadenceDays,
    itemsPerRound: s?.consumption_items_per_round ?? DEFAULT_REMINDER_SETTINGS.itemsPerRound,
    quietHoursStart: s?.quiet_hours?.start ?? DEFAULT_REMINDER_SETTINGS.quietHoursStart,
    quietHoursEnd: s?.quiet_hours?.end ?? DEFAULT_REMINDER_SETTINGS.quietHoursEnd,
  };
}

function settingsToApi(s: ReminderSettings): ApiSettings {
  return {
    timezone: s.timezone,
    consumption_reminders_enabled: s.enabled,
    consumption_cadence_days: s.cadenceDays,
    consumption_items_per_round: s.itemsPerRound,
    quiet_hours: {start: s.quietHoursStart, end: s.quietHoursEnd},
  };
}

/** Answers the UI offers when the server doesn't narrow them per check. */
const DEFAULT_RESPONSES: CheckResponse[] = ['used_up', 'still_have', 'partial', 'not_now'];

function stockFromApi(s: ApiStock | null | undefined): QuickCheckStock | null {
  if (!s) return null;
  return {
    id: s.id,
    productId: s.product_id,
    productName: s.product_name,
    thumbImageUrl: s.thumb_image_url,
    quantityLeft: s.quantity_left == null ? undefined : parseDecimal(s.quantity_left),
    quantityFull: s.quantity_full == null ? undefined : parseDecimal(s.quantity_full),
    unit: s.unit,
    storeName: s.store_name,
    addedAt: s.added_at ? new Date(s.added_at) : undefined,
    isOpen: s.is_open,
    productTotal: s.product_total == null ? undefined : parseDecimal(s.product_total),
    productStockCount: s.product_stock_count,
  };
}

function checkFromApi(c: ApiCheck): QuickCheck {
  const stock = stockFromApi(c.stock);
  return {
    id: c.id,
    state: c.state,
    askedAt: new Date(c.asked_at),
    expiresAt: new Date(c.expires_at),
    // The server sends a rendered question; fall back to a local one so a row
    // is never blank if that field is missing.
    question: c.question ?? (stock ? `Have you used up the ${stock.productName}?` : 'Have you used this up?'),
    questionKey: c.question_key,
    params: c.params,
    stock,
    responses: c.responses?.length ? c.responses : DEFAULT_RESPONSES,
  };
}

@Injectable({providedIn: 'root'})
export class ConsumptionCheckService {
  private readonly http = inject(HttpClient);
  private readonly log: AppLogger = inject(AppLoggerService).createForClass(ConsumptionCheckService);

  /**
   * Open checks for a household.
   *
   * In phase 1 this call also *generates* a round when the household is due,
   * so it is the only thing that makes a check exist — see the feature doc.
   */
  listChecks(householdId: number): Observable<QuickCheck[]> {
    const url = `${environment.apiUrl}/households/${householdId}/consumption-checks`;
    return this.http.get<ApiCheckListResponse>(url).pipe(
      map(res => (res?.checks ?? []).map(checkFromApi)),
    );
  }

  /**
   * Forces a round now, ignoring the cadence, and returns the open checks that
   * result — what the "Check now" button calls. The server runs it on the
   * `pull` channel, so nobody else in the household is notified.
   *
   * May legitimately answer with an empty list: forcing skips the schedule, not
   * the bar a stock row has to clear to be worth asking about.
   */
  runRound(householdId: number): Observable<QuickCheck[]> {
    const url = `${environment.apiUrl}/households/${householdId}/consumption-checks/run`;
    return this.http.post<ApiCheckListResponse>(url, {}).pipe(
      map(res => (res?.checks ?? []).map(checkFromApi)),
    );
  }

  /**
   * Answer one check.
   *
   * `quantityLeft` is required for `partial` and rejected otherwise. The
   * `clientId` makes the call idempotent: a retry after a dropped connection
   * returns the stored answer instead of mutating stock twice.
   */
  respond(
    checkId: number,
    response: CheckResponse,
    opts: {quantityLeft?: number; clientId?: string} = {},
  ): Observable<RespondResult> {
    const url = `${environment.apiUrl}/consumption-checks/${checkId}/respond`;
    const body: Record<string, unknown> = {
      response,
      client_id: opts.clientId ?? crypto.randomUUID(),
    };
    if (response === 'partial') {
      body['quantity_left'] = String(opts.quantityLeft ?? 0);
    }

    this.log.debug?.(`answering check ${checkId} with ${response}`);
    return this.http.post<ApiRespondResponse>(url, body).pipe(
      map(res => ({
        checkId: res?.id ?? checkId,
        response: res?.response ?? response,
        stock: stockFromApi(res?.stock),
      })),
    );
  }

  /**
   * The household's reminder settings. A household that has never changed them
   * has no stored row; the server answers with the defaults, and so do we if it
   * cannot answer at all.
   */
  getSettings(householdId: number): Observable<ReminderSettings> {
    const url = `${environment.apiUrl}/households/${householdId}/settings`;
    return this.http.get<ApiSettings>(url).pipe(map(settingsFromApi));
  }

  /** Replaces the household's reminder settings; returns what was stored. */
  updateSettings(householdId: number, settings: ReminderSettings): Observable<ReminderSettings> {
    const url = `${environment.apiUrl}/households/${householdId}/settings`;
    return this.http.put<ApiSettings>(url, settingsToApi(settings)).pipe(
      // A 204 or an empty body means "stored as sent".
      map(res => (res ? settingsFromApi(res) : settings)),
    );
  }

  /** Mute or unmute a product for this household ("never ask about this"). */
  setProductOptOut(householdId: number, productId: number, muted: boolean): Observable<void> {
    const url = `${environment.apiUrl}/households/${householdId}/reminder-opt-outs/by-product/${productId}`;
    return muted ? this.http.put<void>(url, {}) : this.http.delete<void>(url);
  }
}
